import apiClient from './apiClient';

const whatsappService = {
  async getStatus() {
    return apiClient.get('/stoc/async/whatsapp/status');
  },

  async connect() {
    return apiClient.post('/stoc/async/whatsapp/connect', {});
  },

  async disconnect() {
    return apiClient.post('/stoc/async/whatsapp/disconnect', {});
  },

  async logout() {
    return apiClient.post('/stoc/async/whatsapp/logout', {});
  },

  async listRecipients(params = {}) {
    const query = new URLSearchParams();
    if (params.q) query.append('q', String(params.q));
    if (params.limit !== undefined) query.append('limit', String(params.limit));
    const endpoint = `/stoc/async/whatsapp/recipients${query.toString() ? `?${query.toString()}` : ''}`;
    return apiClient.get(endpoint);
  },

  async sendMessage(payload) {
    return apiClient.post('/stoc/async/whatsapp/send-message', payload);
  },

  // Sender pool (which internal account may send) ranked by who last talked
  // with this contact. Resolves the contact by hash or phone server-side.
  async getSenderInit({ phone, contactHash } = {}) {
    const params = new URLSearchParams();
    if (phone) params.append('phone', phone);
    if (contactHash) params.append('contact_hash', contactHash);
    return apiClient.get(`/stoc/async/whatsapp/sender-init?${params}`);
  },

  // Plain SMS to a RO mobile via ClickPhone (used by the message drawer's
  // SMS channel). Backend rejects non-RO-mobile numbers.
  async sendSms({ phone, message } = {}) {
    return apiClient.post('/stoc/async/sms/send', { phone, message });
  },

  // ── Official Cloud API number (dezshop.bot) ────────────────────────────
  // 24h customer-service window state for a destination number: free text is
  // allowed only while it's open; closed → approved template only.
  async getCloudWindowState(phone) {
    return apiClient.get(`/stoc/async/whatsapp-cloud/window-state?phone=${encodeURIComponent(phone)}`);
  },

  // Templates registered on the WABA (raw Meta list: { data: [...] }).
  async getCloudTemplates() {
    return apiClient.get('/stoc/async/whatsapp-cloud/templates');
  },

  // Upload a file to WhatsApp's media store (for template DOCUMENT/IMAGE
  // header parameters) — returns { media_id, filename }.
  async uploadCloudMedia(file) {
    const form = new FormData();
    form.append('file', file);
    return apiClient.postFormData('/stoc/async/whatsapp-cloud/upload-media', form);
  },

  // Same, but from an image URL (our CDNs serve .webp — the backend converts
  // to JPEG, which is what WhatsApp header images require).
  async uploadCloudMediaFromUrl(url) {
    return apiClient.post('/stoc/async/whatsapp-cloud/upload-media-from-url', { url });
  },

  // Send free-form media (document/image) on the official number by media id
  // (from uploadCloudMedia) — only valid inside the open 24h window.
  async sendCloudMedia({ phone, mediaType = 'document', mediaId, filename, caption } = {}) {
    return apiClient.post('/stoc/async/whatsapp-cloud/send-media', {
      phone,
      media_type: mediaType,
      media_id: mediaId,
      filename,
      caption,
    });
  },

  // Initiate a conversation with an approved template (works cold).
  async sendCloudTemplate({ phone, templateName, language = 'ro', components, contactId } = {}) {
    return apiClient.post('/stoc/async/whatsapp-cloud/send-template', {
      phone,
      template_name: templateName,
      language,
      components,
      contact_id: contactId || undefined,
    });
  },

  // Send a single image or document (PDF/doc/...) to a WhatsApp contact.
  // Multipart upload; backend routes image vs document automatically.
  async sendAttachment({ phone, file, caption, senderUserId } = {}) {
    const form = new FormData();
    form.append('phone', phone);
    form.append('file', file);
    if (caption) form.append('caption', caption);
    if (senderUserId) form.append('sender_user_id', String(senderUserId));
    return apiClient.postFormData('/stoc/async/whatsapp/send-attachment', form);
  },

  // Reply in a partner buyer thread (`channel` 'olx' or 'ovoko'): text,
  // photos, or both. OLX sends them as one message; Ovoko sends the text
  // (translated for the buyer) and then each photo.
  async sendPartnerMessage({ channel, conversationId, message, files = [] } = {}) {
    const form = new FormData();
    form.append('conversation_id', String(conversationId));
    form.append('message', message || '');
    files.forEach((file) => form.append('files', file));
    return apiClient.postFormData(`/stoc/async/whatsapp/inbox/${channel}/send-message`, form);
  },

  async sendDevMessage(payload) {
    return apiClient.post('/stoc/async/whatsapp/dev/send-message', payload);
  },

  async sendInboxMessage(payload) {
    return apiClient.post('/stoc/async/whatsapp/inbox/send-message', {
      conversation_id: payload.conversationId,
      message: payload.message,
    });
  },

  async getHistoryByPhone(phone, { limit = 100, beforeId, ownerUserId } = {}) {
    const params = new URLSearchParams();
    if (phone) params.append('phone', phone);
    if (limit) params.append('limit', limit);
    if (beforeId) params.append('before_id', beforeId);
    if (ownerUserId) params.append('owner_user_id', ownerUserId);
    return apiClient.get(`/stoc/async/whatsapp/history?${params}`);
  },

  async getSyncStatus(phoneDigits) {
    const params = phoneDigits ? `?phone_digits=${phoneDigits}` : '';
    return apiClient.get(`/stoc/async/whatsapp/dev/sync-status${params}`);
  },

  async getAllowedPhones() {
    return apiClient.get('/stoc/async/whatsapp/dev/allowed-phones');
  },

  async getInboxAllowedPhones() {
    return apiClient.get('/stoc/async/whatsapp/inbox/allowed-phones');
  },

  // Per-channel count of conversations (not messages) with unread inbound
  // messages — powers the dashboard "unread per WhatsApp number" card.
  async getInboxUnreadSummary() {
    return apiClient.get('/stoc/async/whatsapp/inbox/unread-summary');
  },

  // Unread inbound counts keyed by phone digits (and by contact id, for chats
  // WhatsApp moved to an @lid address), summed across all our sender accounts.
  // One global map feeds every WhatsApp icon on a page — see useWhatsAppUnread.
  async getUnreadByPhone() {
    return apiClient.get('/stoc/async/whatsapp/unread-by-phone');
  },

  // Threads the agent is working on right now (spinner over the unread counter).
  async getAgentRunning() {
    return apiClient.get('/stoc/async/whatsapp/agent-running');
  },

  async getInbox(phoneDigits, { limit = 30, offset = 0, q, unreadOnly = false, selectedId } = {}) {
    const params = new URLSearchParams();
    if (phoneDigits) params.append('phone_digits', phoneDigits);
    params.append('limit', String(limit));
    params.append('offset', String(offset));
    if (q) params.append('q', q);
    if (unreadOnly) params.append('unread_only', 'true');
    if (selectedId) params.append('selected_id', String(selectedId));
    return apiClient.get(`/stoc/async/whatsapp/inbox?${params}`);
  },

  async getConversationMessages(conversationId, { limit = 50, beforeId } = {}) {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit);
    if (beforeId) params.append('before_id', beforeId);
    return apiClient.get(`/stoc/async/whatsapp/dev/conversation/${conversationId}/messages?${params}`);
  },

  async getInboxConversationMessages(conversationId, { limit = 50, beforeId } = {}) {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit);
    if (beforeId) params.append('before_id', beforeId);
    return apiClient.get(`/stoc/async/whatsapp/inbox/conversation/${conversationId}/messages?${params}`);
  },

  async markChatUnread(userId, chatJid, conversationId, escalate = true) {
    return apiClient.post('/stoc/async/whatsapp/dev/mark-chat-unread', {
      user_id: userId,
      chat_jid: chatJid,
      conversation_id: conversationId,
      escalate,
    });
  },

  async deEscalate(conversationId) {
    return apiClient.post('/stoc/async/whatsapp/dev/de-escalate', {
      conversation_id: conversationId,
    });
  },

  async markInboxChatUnread(userId, chatJid, conversationId, escalate = true) {
    return apiClient.post('/stoc/async/whatsapp/inbox/mark-chat-unread', {
      user_id: userId,
      chat_jid: chatJid,
      conversation_id: conversationId,
      escalate,
    });
  },

  async getProfilePictures(chatJids) {
    return apiClient.post('/stoc/async/whatsapp/inbox/profile-pictures', { chat_jids: chatJids });
  },

  async markInboxChatRead(conversationId) {
    return apiClient.post('/stoc/async/whatsapp/inbox/mark-chat-read', {
      conversation_id: conversationId,
    });
  },

  async deEscalateInbox(conversationId) {
    return apiClient.post('/stoc/async/whatsapp/inbox/de-escalate', {
      conversation_id: conversationId,
    });
  },

  async purgeSyncData() {
    return apiClient.delete('/stoc/async/whatsapp/dev/purge-sync-data');
  },
};

export default whatsappService;
